import { useCallback, useState } from "react";

const NONE_EXPANDED: readonly never[] = [];

/**
 * Expanded state keyed by item id. Lives above the cards so it survives refetches,
 * re-sorts, and optimistic cache swaps that re-render the list.
 */
export const useExpandedIds = <TId extends string>(initial: readonly TId[] = NONE_EXPANDED) => {
	const [expanded, setExpanded] = useState<ReadonlySet<TId>>(() => new Set(initial));

	const setIsExpanded = useCallback((id: TId, isExpanded: boolean) => {
		setExpanded((prev) => {
			if (prev.has(id) === isExpanded) return prev;
			const next = new Set(prev);
			if (isExpanded) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);

	const isExpanded = useCallback((id: TId) => expanded.has(id), [expanded]);

	return { isExpanded, setIsExpanded };
};
